import { useMemo, useState, type CSSProperties } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Button } from '@helios/ui/button';
import { Modal } from '@helios/ui/overlay';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qInventorySuppliersList,
  type InventorySupplierRow,
} from '@helios/data-access';
import { usePowerSyncDb, usePowerSyncQuery } from '../../adapters/powersync';
import { useBrandTokens, type ShellOutletContext } from '../../shell/shared';
import {
  InventoryListToolbar,
  InventoryPageShell,
  InventoryPrimaryAction,
  InventoryRow,
  InventoryStatusBadge,
  InventoryTable,
} from './_shared';

// PRD.207 — Inventory Suppliers list page.
//
// Master data only. Creating/editing/deactivating a supplier never
// touches stock_movements. Suppliers are linked to Purchase Orders and
// Delivery Orders via supplier_id; PO/DO continue to write supplier_name
// as the historical snapshot.

const COLUMNS = [
  { label: 'Supplier', width: '1.4fr' },
  { label: 'Contact Person', width: '1fr' },
  { label: 'Phone', width: '160px' },
  { label: 'Email', width: '1fr' },
  { label: 'Status', width: '120px' },
  { label: 'Actions', width: '120px', align: 'right' as const },
];

interface SupplierFormState {
  supplier_name: string;
  contact_person: string;
  phone: string;
  email: string;
  address: string;
  tax_id: string;
  payment_terms: string;
  notes: string;
  status: 'active' | 'inactive';
}

const EMPTY_FORM: SupplierFormState = {
  supplier_name: '',
  contact_person: '',
  phone: '',
  email: '',
  address: '',
  tax_id: '',
  payment_terms: '',
  notes: '',
  status: 'active',
};

function rowToForm(row: InventorySupplierRow): SupplierFormState {
  return {
    supplier_name: row.supplier_name ?? '',
    contact_person: row.contact_person ?? '',
    phone: row.phone ?? '',
    email: row.email ?? '',
    address: row.address ?? '',
    tax_id: row.tax_id ?? '',
    payment_terms: row.payment_terms ?? '',
    notes: row.notes ?? '',
    status: (row.status === 'inactive' ? 'inactive' : 'active') as 'active' | 'inactive',
  };
}

export function SuppliersPage() {
  const { collapsed = false, onToggleSidebar } = useOutletContext<ShellOutletContext>() ?? {
    collapsed: false,
  };
  const brandTokens = useBrandTokens();
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';

  const { data: rows = [] } = usePowerSyncQuery<InventorySupplierRow>(
    qInventorySuppliersList(),
  );

  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null); // 'new' | id
  const [form, setForm] = useState<SupplierFormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(
      (r) =>
        (r.supplier_name ?? '').toLowerCase().includes(q) ||
        (r.contact_person ?? '').toLowerCase().includes(q) ||
        (r.phone ?? '').toLowerCase().includes(q) ||
        (r.email ?? '').toLowerCase().includes(q),
    );
  }, [rows, search]);

  const openNew = () => {
    setForm(EMPTY_FORM);
    setSaveError(null);
    setEditingId('new');
  };
  const openEdit = (row: InventorySupplierRow) => {
    setForm(rowToForm(row));
    setSaveError(null);
    setEditingId(row.id);
  };
  const closeModal = () => {
    if (saving) return;
    setEditingId(null);
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!db) {
      setSaveError('Local database not ready.');
      return;
    }
    if (!orgId) {
      setSaveError('Missing organization context.');
      return;
    }
    if (!form.supplier_name.trim()) {
      setSaveError('Supplier name is required.');
      return;
    }
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setSaveError('Email looks invalid.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const now = new Date().toISOString();
      if (editingId === 'new') {
        const id = crypto.randomUUID();
        await db.execute(
          `INSERT INTO inventory_suppliers (
            id, org_id, supplier_name, contact_person, phone, email, address,
            tax_id, payment_terms, notes, status, created_at, updated_at
          ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            id,
            orgId,
            form.supplier_name.trim(),
            form.contact_person.trim() || null,
            form.phone.trim() || null,
            form.email.trim() || null,
            form.address.trim() || null,
            form.tax_id.trim() || null,
            form.payment_terms.trim() || null,
            form.notes.trim() || null,
            form.status,
            now,
            now,
          ],
        );
      } else if (editingId) {
        await db.execute(
          `UPDATE inventory_suppliers SET
             supplier_name = ?, contact_person = ?, phone = ?, email = ?,
             address = ?, tax_id = ?, payment_terms = ?, notes = ?,
             status = ?, updated_at = ?
           WHERE id = ?`,
          [
            form.supplier_name.trim(),
            form.contact_person.trim() || null,
            form.phone.trim() || null,
            form.email.trim() || null,
            form.address.trim() || null,
            form.tax_id.trim() || null,
            form.payment_terms.trim() || null,
            form.notes.trim() || null,
            form.status,
            now,
            editingId,
          ],
        );
      }
      setSaving(false);
      setEditingId(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed. Please try again.';
      setSaving(false);
      setSaveError(msg);
    }
  };

  const toggleStatus = async (row: InventorySupplierRow) => {
    if (!db) return;
    const next = row.status === 'inactive' ? 'active' : 'inactive';
    try {
      await db.execute(
        `UPDATE inventory_suppliers SET status = ?, updated_at = ? WHERE id = ?`,
        [next, new Date().toISOString(), row.id],
      );
    } catch {
      // best-effort — toggling is recoverable from the row context.
    }
  };

  return (
    <InventoryPageShell
      title="Suppliers"
      subtitle="Manage suppliers used by Purchase Orders and Delivery Orders."
      collapsed={collapsed}
      onToggleSidebar={onToggleSidebar}
      primaryAction={<InventoryPrimaryAction label="Add Supplier" onClick={openNew} />}
    >
      <InventoryListToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search supplier, contact person, phone, email..."
      />
      <div style={{ height: 'var(--sp-4)', flexShrink: 0 }} />
      <InventoryTable
        columns={COLUMNS}
        empty={
          filtered.length === 0
            ? {
                show: true,
                title: rows.length === 0 ? 'No suppliers yet.' : 'No matching suppliers.',
                hint:
                  rows.length === 0
                    ? 'Add a supplier to start linking Purchase Orders and Delivery Orders.'
                    : 'Try a different search.',
              }
            : undefined
        }
      >
        {filtered.map((row) => (
          <InventoryRow
            key={row.id}
            columns={COLUMNS}
            clickable
            onClick={() => openEdit(row)}
          >
            <div>
              <div
                style={{
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 'var(--font-weight-semi)',
                  color: 'var(--text)',
                }}
              >
                {row.supplier_name}
              </div>
              {row.address && (
                <div
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--text-muted)',
                  }}
                >
                  {row.address}
                </div>
              )}
            </div>
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>
              {row.contact_person ?? '—'}
            </span>
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
              {row.phone ?? '—'}
            </span>
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
              {row.email ?? '—'}
            </span>
            <InventoryStatusBadge
              status={row.status === 'inactive' ? 'inactive' : 'active'}
              label={row.status === 'inactive' ? 'Inactive' : 'Active'}
            />
            <div style={{ display: 'flex', gap: 'var(--sp-1)', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  void toggleStatus(row);
                }}
                style={{
                  height: 28,
                  padding: '0 10px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border)',
                  background: 'var(--bg-surface)',
                  color: 'var(--text)',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                {row.status === 'inactive' ? 'Activate' : 'Deactivate'}
              </button>
            </div>
          </InventoryRow>
        ))}
      </InventoryTable>

      {editingId && (
        <SupplierFormModal
          mode={editingId === 'new' ? 'new' : 'edit'}
          form={form}
          saving={saving}
          saveError={saveError}
          portalStyle={brandTokens as CSSProperties}
          onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
          onClose={closeModal}
          onSave={handleSave}
        />
      )}
    </InventoryPageShell>
  );
}

// ─── Supplier Form Modal ───

function SupplierFormModal({
  mode,
  form,
  saving,
  saveError,
  portalStyle,
  onChange,
  onClose,
  onSave,
}: {
  mode: 'new' | 'edit';
  form: SupplierFormState;
  saving: boolean;
  saveError: string | null;
  portalStyle: CSSProperties;
  onChange: (patch: Partial<SupplierFormState>) => void;
  onClose: () => void;
  onSave: () => void;
}) {
  return (
    <Modal
      open
      onOpenChange={(v) => !v && onClose()}
      title={mode === 'new' ? 'Add Supplier' : 'Edit Supplier'}
      maxWidth={620}
      portalStyle={portalStyle}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Button variant="outline" size="md" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="gradient" size="md" onClick={onSave} disabled={saving}>
            {saving ? 'Saving…' : mode === 'new' ? 'Save Supplier' : 'Save Changes'}
          </Button>
        </div>
      }
    >
      <div
        style={{
          padding: 'var(--sp-3) var(--sp-5) var(--sp-5)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-3)',
        }}
      >
        {saveError && (
          <div
            role="alert"
            style={{
              padding: 'var(--sp-2) var(--sp-3)',
              borderRadius: 'var(--radius-md)',
              background: '#fee2e2',
              color: '#991b1b',
              border: '1px solid #fecaca',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            {saveError}
          </div>
        )}
        <Field
          label="Supplier Name"
          required
          value={form.supplier_name}
          onChange={(v) => onChange({ supplier_name: v })}
        />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-3)' }}>
          <Field
            label="Contact Person"
            value={form.contact_person}
            onChange={(v) => onChange({ contact_person: v })}
          />
          <Field
            label="Phone"
            value={form.phone}
            onChange={(v) => onChange({ phone: v })}
          />
        </div>
        <Field
          label="Email"
          value={form.email}
          onChange={(v) => onChange({ email: v })}
          type="email"
        />
        <Field
          label="Address"
          value={form.address}
          onChange={(v) => onChange({ address: v })}
        />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-3)' }}>
          <Field
            label="TIN / Tax ID"
            value={form.tax_id}
            onChange={(v) => onChange({ tax_id: v })}
          />
          <Field
            label="Payment Terms"
            value={form.payment_terms}
            onChange={(v) => onChange({ payment_terms: v })}
            placeholder="e.g. Net 30"
          />
        </div>
        <Field
          label="Notes"
          value={form.notes}
          onChange={(v) => onChange({ notes: v })}
          multiline
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
          <label
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--text-muted)',
            }}
          >
            Status
          </label>
          <select
            value={form.status}
            onChange={(e) =>
              onChange({ status: e.target.value === 'inactive' ? 'inactive' : 'active' })
            }
            style={{
              height: 'var(--input-height)',
              padding: '0 var(--input-padding-x)',
              borderRadius: 'var(--input-radius)',
              border: '1px solid var(--border-input)',
              background: 'var(--input-bg)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--input-text)',
            }}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>
    </Modal>
  );
}

function Field({
  label,
  value,
  onChange,
  required,
  placeholder,
  type = 'text',
  multiline,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  placeholder?: string;
  type?: string;
  multiline?: boolean;
}) {
  const inputStyle: CSSProperties = {
    height: multiline ? undefined : 'var(--input-height)',
    minHeight: multiline ? 80 : undefined,
    padding: multiline ? '8px var(--input-padding-x)' : '0 var(--input-padding-x)',
    borderRadius: 'var(--input-radius)',
    border: '1px solid var(--border-input)',
    background: 'var(--input-bg)',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--input-text)',
    outline: 'none',
    fontFamily: 'inherit',
    resize: multiline ? 'vertical' : undefined,
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
      <label
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--text-muted)',
        }}
      >
        {label}
        {required && <span style={{ color: '#dc2626' }}> *</span>}
      </label>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={inputStyle}
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={inputStyle}
        />
      )}
    </div>
  );
}
